import { useI18n } from '../../../i18n';
// ============================================
// TransactionFilterBar Component
// Filter bar with Location, Status, DateRange dropdowns + View toggle
// ============================================

import { SelectDropdown } from '../../../components/shared/SelectDropdown';
import {
  LocationIcon,
  FilterIcon,
  CalendarIcon,
  GridIcon,
  ListIcon,
} from '../../../components/icons';
import { cn } from '../../../utils/cn';

// ============================================
// Types
// ============================================

export interface TransactionFilters {
  location: string | null;
  status: string | null;
  dateRange: string;
  /**
   * Khoảng ngày tự chọn, chỉ dùng khi `dateRange === 'custom'`. Dạng `YYYY-MM-DD`
   * (đúng giá trị của `<input type="date">`), chuỗi rỗng = chưa chọn.
   *
   * Tách khỏi `dateRange` thay vì nhét chuỗi "2026-01-01..2026-02-01" vào đó:
   * mọi chỗ đọc `dateRange` đều đang so sánh với các preset, nhồi thêm một
   * định dạng lạ là bắt tất cả phải biết tự tách chuỗi.
   */
  dateFrom?: string;
  dateTo?: string;
}

interface TransactionFilterBarProps {
  filters: TransactionFilters;
  onFilterChange: (filters: TransactionFilters) => void;
  viewMode: 'table' | 'grid';
  onViewModeChange: (mode: 'table' | 'grid') => void;
  /** List of store locations for dropdown */
  locations?: Array<{ id: string; name: string }>;
  className?: string;
}

// ============================================
// Constants
// ============================================

// `STATUS_OPTIONS` và `DATE_RANGE_OPTIONS` viết cứng tiếng Anh đã GỠ (09/09):
// không nơi nào dùng — component tự dựng `statusOptions`/`dateRangeOptions` qua
// `t()` trong thân hàm. Giữ lại thì thành bẫy: vừa thêm tuỳ chọn "custom" vào
// bản thật, bản chết vẫn thiếu, ai đọc trước cũng tưởng đó là danh sách đúng.

// ============================================
// Component
// ============================================

export function TransactionFilterBar({
  filters,
  onFilterChange,
  viewMode,
  onViewModeChange,
  locations = [],
  className,
}: TransactionFilterBarProps) {
  const { t } = useI18n();
  const locationOptions = [
    { value: '', label: t('allLocations') },
    ...locations.map((loc) => ({ value: loc.id, label: loc.name })),
  ];

  const statusOptions = [
    { value: '', label: t('allStatuses') },
    { value: 'COMPLETED', label: t('completed') || 'Completed' },
    { value: 'PROCESSING', label: t('processing') },
    { value: 'FAILED', label: t('declined') || 'Declined' },
    { value: 'REFUNDED', label: t('refunded') },
    { value: 'PENDING', label: t('pending') || 'Pending' },
  ];

  const dateRangeOptions = [
    { value: 'all', label: t('all') || 'All' },
    { value: 'today', label: t('today') || 'Today' },
    { value: 'last_7_days', label: t('last7Days') },
    { value: 'last_30_days', label: t('last30Days') },
    { value: 'last_90_days', label: t('last90Days') },
    { value: 'this_year', label: t('thisYear') },
    { value: 'custom', label: t('customRange') },
  ];

  const isCustom = filters.dateRange === 'custom';

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-xl bg-[#F3F3FE] px-4 py-3 sm:flex-row sm:items-center sm:justify-between',
        className
      )}
    >
      {/* Left side - Filters */}
      <div className="flex flex-1 flex-wrap items-center gap-3">
        {/* Location Filter */}
        <div className="relative w-full min-w-0 sm:w-auto sm:min-w-[180px]">
          <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
            <LocationIcon size={16} className="text-slate-400" />
          </div>
          <SelectDropdown
            value={filters.location || ''}
            onChange={(value) =>
              onFilterChange({ ...filters, location: value || null })
            }
            options={locationOptions}
            className="pl-9"
          />
        </div>

        {/* Status + Date Range: 1 hàng 2 cột trên mobile, tách rời trên desktop */}
        <div className="grid w-full grid-cols-2 gap-3 sm:contents">
          {/* Status Filter */}
          <div className="relative min-w-0 sm:w-auto sm:min-w-[160px]">
            <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
              <FilterIcon size={16} className="text-slate-400" />
            </div>
            <SelectDropdown
              value={filters.status || ''}
              onChange={(value) =>
                onFilterChange({ ...filters, status: value || null })
              }
              options={statusOptions}
              className="pl-9"
            />
          </div>

          {/* Date Range Filter */}
          <div className="relative min-w-0 sm:w-auto sm:min-w-[160px]">
            <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
              <CalendarIcon size={16} className="text-slate-400" />
            </div>
            <SelectDropdown
              value={filters.dateRange}
              onChange={(value) =>
                onFilterChange({ ...filters, dateRange: value })
              }
              options={dateRangeOptions}
              className="pl-9"
            />
          </div>
        </div>

        {/* Hai ô ngày chỉ hiện khi chọn "Khoảng tuỳ chọn" — luôn hiện thì
            chiếm chỗ và gây hiểu nhầm là đang lọc theo chúng. */}
        {isCustom && (
          <div className="flex w-full min-w-0 items-center gap-2 sm:w-auto">
            <input
              type="date"
              value={filters.dateFrom ?? ''}
              // `max` chặn ngay ở trình duyệt, khỏi để người dùng chọn xong mới
              // báo "từ ngày sau đến ngày".
              max={filters.dateTo || undefined}
              onChange={(e) => onFilterChange({ ...filters, dateFrom: e.target.value })}
              aria-label={t('fromDate')}
              className="h-10 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary-400 sm:w-[150px] sm:flex-none"
            />
            <span className="shrink-0 text-sm text-slate-400">{t('to') || '→'}</span>
            <input
              type="date"
              value={filters.dateTo ?? ''}
              min={filters.dateFrom || undefined}
              onChange={(e) => onFilterChange({ ...filters, dateTo: e.target.value })}
              aria-label={t('toDate')}
              className="h-10 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary-400 sm:w-[150px] sm:flex-none"
            />
          </div>
        )}
      </div>

      {/* Right side - View Toggle */}
      <div className="flex w-fit items-center gap-1 self-end rounded-lg bg-white p-1 shadow-sm sm:self-auto">
        <button
          onClick={() => onViewModeChange('grid')}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-md transition-colors',
            viewMode === 'grid'
              ? 'bg-primary-600 text-white'
              : 'text-slate-400 hover:text-slate-600'
          )}
          aria-label={t('gridView')}
        >
          <GridIcon size={18} />
        </button>
        <button
          onClick={() => onViewModeChange('table')}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-md transition-colors',
            viewMode === 'table'
              ? 'bg-primary-600 text-white'
              : 'text-slate-400 hover:text-slate-600'
          )}
          aria-label={t('listView')}
        >
          <ListIcon size={18} />
        </button>
      </div>
    </div>
  );
}
